import { useMemo } from 'react'

import { useSuspenseQuery } from '@tanstack/react-query'
import { useSearch } from '@tanstack/react-router'

import {
  type GlobalAssignment,
  globalAssignments,
  type RegionAssignment,
  regionAssignments,
} from '@/model/composite/regionAssignments'
import { assignmentsListQuery } from '@/state/assignments/assignments'
import { operatingRegionsQuery } from '@/state/regions/regions'
import { type Region } from '@/state/regions/types'

export type AssignmentType = 'region' | 'global'

export interface UseAssignmentsPageReturn {
  assignments: RegionAssignment[] | GlobalAssignment[]
  regions: Region[]
  assignmentType: AssignmentType
}

export const useAssignmentsPage = (): UseAssignmentsPageReturn => {
  const search = useSearch({ from: '/assignments/' })
  const assignmentType: AssignmentType = (search.assignment_type as AssignmentType) ?? 'region'

  // Fetch assignments for the selected region/type (or all if none selected)
  const { data: assignmentsSnapshot } = useSuspenseQuery(
    assignmentsListQuery({
      regionId: assignmentType === 'region' ? search.region_id : undefined,
      assignmentType,
      limit: search.limit,
    })
  )
  // Fetch all region assignments (unfiltered) to determine which regions have assignments
  const { data: allAssignmentsSnapshot } = useSuspenseQuery(
    assignmentsListQuery({ assignmentType: 'region', limit: 200 })
  )
  const { data: regionsSnapshot } = useSuspenseQuery(operatingRegionsQuery())

  const assignments = assignmentsSnapshot.data
  const allAssignments = allAssignmentsSnapshot.data
  const regions = regionsSnapshot.data

  // Apply composite function to join assignments with regions based on type
  const processedAssignments = useMemo(() => {
    if (assignmentType === 'global') {
      return globalAssignments(assignments, regions)
    }
    return regionAssignments(assignments, regions)
  }, [assignments, regions, assignmentType])

  // Filter regions to only those with region-type assignments (for selector)
  const filteredRegions = useMemo(
    () =>
      regions.filter((r) =>
        allAssignments.some((a) => {
          if (Array.isArray(a.regionId)) {
            return a.regionId.includes(r._id)
          }
          return a.regionId === r._id
        })
      ),
    [allAssignments, regions]
  )

  return {
    assignments: processedAssignments,
    regions: filteredRegions,
    assignmentType,
  }
}
